import { useState } from 'react';
import { WandSparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { hashArtifactAiText, type ArtifactAiTargetSeed } from './ArtifactAiPanel';
import type { AiSuggestBody, AiSuggestionTargetIntent } from '@/lib/api';

type SuggestTargetKind = 'replace' | 'insert';

interface ArtifactAiSuggestTabProps {
  targetSeed: ArtifactAiTargetSeed;
  disabled: boolean;
  isStreaming: boolean;
  onSuggest: (body: AiSuggestBody) => Promise<void>;
}

export function ArtifactAiSuggestTab({
  targetSeed,
  disabled,
  isStreaming,
  onSuggest,
}: ArtifactAiSuggestTabProps): React.ReactElement {
  const [instruction, setInstruction] = useState('');
  const [targetKind, setTargetKind] = useState<SuggestTargetKind>(
    targetSeed.anchorBlockIndex >= 0 ? 'replace' : 'insert'
  );
  const replaceUnavailable = targetSeed.anchorBlockIndex < 0;
  const canSubmit = !disabled && !isStreaming && instruction.trim().length > 0;

  const submitSuggestion = (): void => {
    const trimmed = instruction.trim();
    if (trimmed.length === 0) return;
    void buildTarget(targetKind, targetSeed)
      .then(target => {
        const body: AiSuggestBody = { instruction: trimmed, target };
        setInstruction('');
        return onSuggest(body);
      })
      .catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="inline-flex w-fit rounded-md border border-border bg-surface/60 p-1">
        <button
          type="button"
          disabled={disabled || isStreaming || replaceUnavailable}
          aria-pressed={targetKind === 'replace'}
          className={
            targetKind === 'replace'
              ? 'rounded-sm bg-[oklch(0.72_0.14_225/0.15)] px-3 py-1 text-xs font-medium text-accent-bright'
              : 'rounded-sm px-3 py-1 text-xs font-medium text-text-secondary hover:text-text-primary disabled:opacity-50'
          }
          onClick={() => {
            setTargetKind('replace');
          }}
        >
          Replace
        </button>
        <button
          type="button"
          disabled={disabled || isStreaming}
          aria-pressed={targetKind === 'insert'}
          className={
            targetKind === 'insert'
              ? 'rounded-sm bg-[oklch(0.72_0.14_225/0.15)] px-3 py-1 text-xs font-medium text-accent-bright'
              : 'rounded-sm px-3 py-1 text-xs font-medium text-text-secondary hover:text-text-primary'
          }
          onClick={() => {
            setTargetKind('insert');
          }}
        >
          Insert after
        </button>
      </div>
      <div className="rounded-md border border-border bg-surface/40 p-2 text-xs text-text-secondary">
        {targetSeed.label}
      </div>
      <label className="sr-only" htmlFor="artifact-ai-suggest-instruction">
        Suggest instruction
      </label>
      <Textarea
        id="artifact-ai-suggest-instruction"
        value={instruction}
        disabled={disabled || isStreaming}
        placeholder="Suggest an edit"
        onChange={event => {
          setInstruction(event.target.value);
        }}
        className="min-h-28"
      />
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={!canSubmit}
          className="inline-flex gap-1.5"
          onClick={submitSuggestion}
        >
          <WandSparkles className="size-4" />
          {isStreaming ? 'Suggesting…' : 'Suggest'}
        </Button>
      </div>
    </div>
  );
}

async function buildTarget(
  targetKind: SuggestTargetKind,
  targetSeed: ArtifactAiTargetSeed
): Promise<AiSuggestionTargetIntent> {
  if (targetKind === 'insert' || targetSeed.anchorBlockIndex < 0) {
    return { kind: 'insert', afterBlockIndex: targetSeed.anchorBlockIndex };
  }
  return {
    kind: 'replace',
    anchorBlockIndex: targetSeed.anchorBlockIndex,
    anchorTextHash: await hashArtifactAiText(targetSeed.text),
  };
}
